import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Search,
  Users,
  CheckSquare,
  Briefcase,
  Lock,
  LogOut,
} from 'lucide-react';
import styles from './CommandPalette.module.css';
import { useAuth } from '../context/AuthContext';
import { fullNavMap } from '../config/modules';
import { useSearchIndex } from '../hooks/useSearchIndex';

const TYPE_ICON = {
  creator: Users,
  task: CheckSquare,
  'team member': Briefcase,
  account: Lock,
};

// The jump list is the user's own map, not a fixed one: it was a hardcoded
// fourteen for everybody, so a marketer could Ctrl-K straight to Revenue and a
// chatter to the Team page — destinations their role's rooms don't contain and
// the API refuses. fullNavMap is the same source the sidebar and mobile drawer
// read, so the palette can never drift from them.
//
// Deduped by path because a couple of pages deliberately appear in two rooms
// (the content plan is in both Content and Operations).
function navActions(user) {
  const seen = new Set();
  const actions = [];
  for (const group of fullNavMap(user)) {
    for (const item of group.items) {
      if (seen.has(item.path)) continue;
      seen.add(item.path);
      actions.push({ label: `Go to ${item.label}`, href: item.path, icon: item.icon || Search });
    }
  }
  return actions;
}

function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { load, search } = useSearchIndex();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      load();
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open, load]);

  const actions = useMemo(() => navActions(user), [user]);

  const results = useMemo(() => {
    if (!query.trim()) {
      return [{ group: 'Actions', items: actions.map((a) => ({ ...a, onSelect: () => navigate(a.href) })) }];
    }
    const entityMatches = search(query);
    const actionMatches = actions.filter((a) => a.label.toLowerCase().includes(query.toLowerCase()));
    const groups = [];
    if (entityMatches.length) {
      groups.push({
        group: 'Results',
        items: entityMatches.slice(0, 20).map((item) => ({
          label: item.title,
          subtitle: item.subtitle,
          icon: TYPE_ICON[item.type] || Search,
          onSelect: () => navigate(item.href),
        })),
      });
    }
    if (actionMatches.length) {
      groups.push({ group: 'Actions', items: actionMatches.map((a) => ({ ...a, onSelect: () => navigate(a.href) })) });
    }
    return groups;
  }, [query, search, navigate, actions]);

  const flatItems = useMemo(() => results.flatMap((g) => g.items), [results]);

  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, flatItems.length - 1));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        flatItems[activeIndex]?.onSelect();
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, flatItems, activeIndex]);

  let runningIndex = -1;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            className={styles.panel}
            role="dialog"
            aria-modal="true"
            aria-label="Command palette"
            initial={{ opacity: 0, scale: 0.96, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -8 }}
            transition={{ duration: 0.16 }}
          >
            <div className={styles.inputRow}>
              <Search size={18} color="var(--text-muted)" />
              <input
                ref={inputRef}
                className={styles.input}
                placeholder="Search creators, tasks, team, or jump to a page..."
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
              />
              <span className={styles.hint}>Esc</span>
            </div>
            <div className={styles.results}>
              {flatItems.length === 0 && <div className={styles.empty}>No results</div>}
              {results.map((group) => (
                <div key={group.group}>
                  <div className={styles.groupLabel}>{group.group}</div>
                  {group.items.map((item) => {
                    runningIndex += 1;
                    const idx = runningIndex;
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.label + idx}
                        className={[styles.item, idx === activeIndex ? styles.active : ''].filter(Boolean).join(' ')}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => {
                          item.onSelect();
                          onClose();
                        }}
                      >
                        <Icon size={16} />
                        <span>
                          <div className={styles.itemTitle}>{item.label}</div>
                          {item.subtitle && <div className={styles.itemSubtitle}>{item.subtitle}</div>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
              <div>
                <div className={styles.groupLabel}>Session</div>
                <button
                  className={styles.item}
                  onClick={() => {
                    logout();
                    onClose();
                  }}
                >
                  <LogOut size={16} />
                  <span className={styles.itemTitle}>Log out</span>
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default CommandPalette;
